import { useMemo } from 'react'

import {
  Icon3dCubeSphere,
  IconBucketDroplet,
  IconBuilding,
  IconBuildingBank,
  IconCalendarEvent,
  IconChartBar,
  IconChartPie,
  IconCurrencyDollar,
  IconDroplet,
  IconFileText,
  IconHome2,
  IconLayoutDashboard,
  IconLayoutList,
  IconLibrary,
  IconPercentage,
  IconSpy,
  IconTransformFilled,
  IconUsers,
} from '@tabler/icons-react'
import { useMatches } from '@tanstack/react-router'

import { useAdminFunds } from '@repo/common/queries/admin/fund'
import type { Fund } from '@repo/common/queries/admin/fund/types'
import type { AdminRedeemable } from '@repo/common/queries/admin/redeem/types'

import { IconRedeem } from '@/components/icons/IconRedeem'

import { useFundContext } from './useFundContext'
import { useRedeemableContext } from './useRedeemableContext'

export type MenuItem = {
  icon?: typeof IconHome2
  label: string
  to?: string
  disabled?: boolean
  active?: boolean
  separator?: boolean
}

export type RootMenuItem = MenuItem & {
  secondary?: MenuItem[]
  rootSecondary?: MenuItem[]
  contains?: string[]
}

export type Menus = {
  main: MenuItem[]
  secondary: MenuItem[]
  mainActiveIdx: number | null
  secondaryActiveIdx: number | null
}

export function useDynamicMenuItems(): Menus {
  const { data: fundsData } = useAdminFunds()
  const fundContext = useFundContext()
  const redeemableContext = useRedeemableContext()
  const matches = useMatches()
  const currentPath =
    matches.length > 0 ? matches[matches.length - 1]!.pathname : '/'
  const primaryFunds = useMemo(
    () =>
      fundsData?.funds.nodes.filter((f) =>
        f.feeders?.some((feeder) => feeder.tradable && feeder.published),
      ) || [],
    [fundsData?.funds.nodes],
  )
  return useMemo(() => {
    const fundContextIsPrimary = fundContext.fundId
      ? primaryFunds.some((f) => f.id === fundContext.fundId)
      : false

    // the inner items for a fund depend on that fund so i use a context (which still comes from the path tho)
    const fundItems: MenuItem[] =
      fundContext.isInFundContext && fundContext.fundId
        ? generateFundMenuItems(
            fundContext.fundId,
            fundContext.fundData,
            fundContext.isLoadingFund,
          )
        : []

    // the all-funds button is the parent for any non-primary fund context
    const allFunds: RootMenuItem = {
      icon: IconBuilding,
      label: 'All Funds',
      to: '/funds',
      contains: ['/fund/'],
      secondary: !fundContextIsPrimary ? fundItems : [],
    }

    const primaryFundLinks = primaryFunds.map((f) => ({
      icon: Icon3dCubeSphere,
      label: f.name || `Fund ${f.id.toUpperCase()}`,
      to: `/fund/${f.id}`,
      secondary: f.id == fundContext.fundId ? fundItems : [],
    }))

    const mainItems: RootMenuItem[] = [
      {
        icon: IconHome2,
        label: 'Home',
        to: '/',
        secondary: [
          {
            icon: IconLibrary,
            label: 'Accounts',
            to: '/',
          },
        ],
      },
      allFunds,
      ...primaryFundLinks,
      {
        icon: IconCurrencyDollar,
        label: 'Asset Pricing',
        to: '/asset-pricing',
      },
      { icon: IconUsers, label: 'Users', to: '/users' },
      { icon: IconSpy, label: 'Audit Log', to: '/audit' },
      {
        icon: IconRedeem,
        label: 'Redeem',
        to: '/redeem',
        contains: ['/redeem/'],
        rootSecondary: [
          {
            icon: IconLayoutList,
            label: 'Redeemables',
            to: '/redeem',
          },
          {
            icon: IconBucketDroplet,
            label: 'Buckets',
            to: '/redeem/buckets',
          },
        ],
        secondary:
          redeemableContext.isInRedeemableContext &&
          redeemableContext.redeemableId
            ? generateRedeemableMenuItems(
                redeemableContext.redeemableId,
                redeemableContext.redeemableData,
                redeemableContext.redemptionId,
              )
            : undefined,
      },
    ]
    return resolveMenus(mainItems, currentPath)
  }, [
    primaryFunds,
    fundContext.isInFundContext,
    fundContext.fundId,
    fundContext.fundData,
    fundContext.isLoadingFund,
    redeemableContext.isInRedeemableContext,
    redeemableContext.redeemableId,
    redeemableContext.redeemableData,
    redeemableContext.redemptionId,
    currentPath,
  ])
}

function generateFundMenuItems(
  fundId: string,
  fundData: Fund | undefined,
  isLoading: boolean,
): MenuItem[] {
  const fundName = fundData?.name || `Fund ${fundId.toUpperCase()}`
  const hasVault = fundData?.feeders?.some((feeder) => !!feeder.vault?.id)
  const hasTradableFeeder = fundData?.feeders?.some((feeder) => feeder.tradable)

  return [
    {
      label: fundName,
    },
    {
      icon: Icon3dCubeSphere,
      label: 'Summary',
      to: `/fund/${fundId}`,
    },
    {
      icon: IconChartPie,
      label: `Assets`,
      to: `/fund/${fundId}/assets`,
    },
    {
      icon: IconUsers,
      label: `Registry`,
      to: `/fund/${fundId}/registry`,
      disabled: !hasTradableFeeder,
    },
    {
      icon: IconTransformFilled,
      label: `Trade`,
      to: `/fund/${fundId}/record-transaction`,
    },
    {
      icon: IconBuildingBank,
      label: 'Vault',
      to: `/fund/${fundId}/vault`,
      disabled: !hasVault || isLoading,
    },
    {
      icon: IconChartBar,
      label: `NAV History`,
      to: `/fund/${fundId}/nav`,
    },
  ]
}

function generateRedeemableMenuItems(
  redeemableId: string,
  _: AdminRedeemable | undefined,
  redemptionId: string | null,
): MenuItem[] {
  return [
    {
      label: redeemableId,
    },
    {
      icon: IconLayoutDashboard,
      label: 'Summary',
      to: `/redeem/${redeemableId}`,
    },
    {
      icon: IconCalendarEvent,
      label: 'Underlying',
      to: `/redeem/${redeemableId}/underlying`,
    },
    {
      icon: IconDroplet,
      label: 'Liquidity',
      to: `/redeem/${redeemableId}/liquidity`,
    },
    {
      icon: IconPercentage,
      label: 'Fees',
      to: `/redeem/${redeemableId}/fees`,
    },
    ...(redemptionId
      ? [
          {
            icon: IconFileText,
            label: 'Redemption',
            to: `/redeem/${redeemableId}/${redemptionId}`,
          },
        ]
      : []),
  ]
}

function buildCombinedSecondary(item: RootMenuItem): MenuItem[] {
  const contextItems = item.secondary || []
  const rootItems = item.rootSecondary || []
  return [
    ...contextItems,
    ...(contextItems.length > 0 && rootItems.length > 0
      ? [{ label: '', separator: true } as MenuItem]
      : []),
    ...rootItems,
  ]
}

function resolveMenus(mainItems: RootMenuItem[], currentPath: string): Menus {
  let mainActiveIdx: number | null = null
  let secondaryActiveIdxTuple: [number, number] | null = null

  currentPath = currentPath.replace(/(?<=.)\/$/, '')
  if (currentPath == '') currentPath = '/'

  for (const [idx, item] of mainItems.entries()) {
    if (item.to === currentPath && secondaryActiveIdxTuple === null) {
      mainActiveIdx = idx
      secondaryActiveIdxTuple = null
    }

    const combined = buildCombinedSecondary(item)
    for (const [subIdx, subItem] of combined.entries()) {
      if (subItem.to === currentPath) {
        mainActiveIdx = idx
        secondaryActiveIdxTuple = [idx, subIdx]
      }
    }
  }

  let secondary: MenuItem[] = []
  if (mainActiveIdx !== null) {
    const combined = buildCombinedSecondary(mainItems[mainActiveIdx]!)
    secondary = combined.map((item, idx) => ({
      ...item,
      active:
        secondaryActiveIdxTuple?.length && idx === secondaryActiveIdxTuple[1],
    }))
  }

  return {
    main: mainItems.map((item, idx) => ({
      ...item,
      active: idx === mainActiveIdx,
    })),
    secondary,
    mainActiveIdx: mainActiveIdx,
    secondaryActiveIdx: secondaryActiveIdxTuple && secondaryActiveIdxTuple[1],
  }
}
